import React from 'react';
import { ChildRoot, PlaceholderImg, Root } from './styles';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'intersection-observer';
import parseRatio from '../primitives/parseRatio';
import { THEME_LAYOUT_TYPES } from '../../types';
import { cx } from '@linaria/core';
import { useTheme } from '../ThemeProvider/ThemeProvider';
if (ENVIRONMENT_TYPE === ENVIRONMENT_TYPES.CLIENT) {
    /* Polyfill to bypass Feature-Detection in react-lazy-load-image-component
     * https://github.com/Aljullu/react-lazy-load-image-component/blob/master/src/utils/intersection-observer.js
     * */
    if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.IntersectionObserverEntry.prototype.isIntersecting = {};
    }
}
export function getImageAttributes(ratioX, ratioY, aspects) {
    // set image attributes to currently needed values
    const imageAttributes = {
        ratio: `aspect${ratioX}x${ratioY}`,
        src: null,
        srcset: null,
        sizes: null,
        alt: null,
        title: null,
    };
    // Check if we have values for current ratio
    if (aspects[imageAttributes.ratio]) {
        imageAttributes.src = aspects[imageAttributes.ratio].src;
        imageAttributes.srcset = aspects[imageAttributes.ratio].srcset;
        imageAttributes.sizes = aspects[imageAttributes.ratio].sizes;
        imageAttributes.alt = aspects[imageAttributes.ratio].alt;
        imageAttributes.title = aspects[imageAttributes.ratio].title;
    }
    // Check for 16x9 Fallback
    else if (aspects.aspect16x9) {
        imageAttributes.src = aspects.aspect16x9.src;
        imageAttributes.srcset = aspects.aspect16x9.srcset;
        imageAttributes.sizes = aspects.aspect16x9.sizes;
        imageAttributes.alt = aspects.aspect16x9.alt;
        imageAttributes.title = aspects.aspect16x9.title;
    }
    // Check if there is any other ratio we could use as Fallback
    else {
        for (const item in aspects) {
            if (aspects[item]) {
                imageAttributes.src = aspects[item].src;
                imageAttributes.srcset = aspects[item].srcset;
                imageAttributes.sizes = aspects[item].sizes;
                imageAttributes.alt = aspects[item].alt;
                imageAttributes.title = aspects[item].title;
                break;
            }
        }
    }
    return imageAttributes;
}
export function Image({ src, srcset, sizes, ratio = '16x9', title, alt, isLoadingEagerly = false, aspect24x9, aspect16x9, aspect16x7, aspect3x4, aspect1x1, isRound, onVisible, isLoading, }) {
    /** Variables */
    const { layout } = useTheme();
    const [ratioX, ratioY] = parseRatio(ratio || '16x9');
    const imageAttributes = getImageAttributes(ratioX, ratioY, {
        aspect24x9,
        aspect16x9,
        aspect16x7,
        aspect3x4,
        aspect1x1,
    });
    const renderedTitle = imageAttributes?.title || title;
    const renderedAlt = imageAttributes?.alt || alt;
    const renderedSrc = imageAttributes.src || src;
    const renderedSrcSet = imageAttributes.srcset || srcset;
    //0 1000px 100px 10
    /** JSX */
    return (React.createElement("div", { className: cx(Root.base, Root.ratio[ratio], isRound && Root.isRound, isLoading && Root.isLoading, isLoading && layout === THEME_LAYOUT_TYPES.CHILD && ChildRoot.isLoading) }, !isLoading && (React.createElement(LazyLoadImage, { placeholder: React.createElement("div", { className: cx(PlaceholderImg.base), title: renderedTitle }), visibleByDefault: isLoadingEagerly, threshold: 100, src: renderedSrc, srcSet: renderedSrcSet, sizes: imageAttributes.sizes || sizes, alt: renderedAlt, title: renderedTitle, onLoad: onVisible, itemProp: "image" }))));
}
export default React.memo(Image);
